import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

export const LEGACY_WEB_ROUTE_MIGRATIONS = [
  {
    legacyPath: "/tasks",
    destination: "WORK_ITEMS",
    message: "旧待办入口已合并到当前工作入口，系统已为你打开新页面。",
  },
  {
    legacyPath: "/workdesk",
    destination: "WORK_ITEMS",
    message: "旧工作台入口已合并到当前工作入口，系统已为你打开新页面。",
  },
  {
    legacyPath: "/sessions",
    destination: "FRONTDESK",
    message: "旧台单入口已合并到前台，系统已为你打开新页面。",
  },
  {
    legacyPath: "/coaches/workbench",
    destination: "COACH",
    message: "旧助教工作台入口已合并到当前助教工作台，系统已为你打开新页面。",
  },
] as const;

export type LegacyWebRouteMigration = (typeof LEGACY_WEB_ROUTE_MIGRATIONS)[number];
export type LegacyWebRoutePath = LegacyWebRouteMigration["legacyPath"];

export function legacyWebRouteMigration(path: string): LegacyWebRouteMigration | undefined {
  return LEGACY_WEB_ROUTE_MIGRATIONS.find((migration) => migration.legacyPath === path);
}

export const LEGACY_GATEWAY_READ_ROUTE_MIGRATIONS = [
  {
    legacyRoute: "GET /v1/tasks",
    legacyPath: "/v1/tasks",
    canonicalRoute: "GET /v1/service-work-items",
    replacementPath: "/v1/service-work-items",
    code: "LEGACY_TASKS_ROUTE_RETIRED",
    message: "旧待办接口已停止使用，请改用当前服务工作项接口。",
  },
  {
    legacyRoute: "GET /v1/workdesk",
    legacyPath: "/v1/workdesk",
    canonicalRoute: "GET /v1/service-work-items",
    replacementPath: "/v1/service-work-items",
    code: "LEGACY_WORKDESK_ROUTE_RETIRED",
    message: "旧工作台接口已停止使用，请改用当前服务工作项接口。",
  },
  {
    legacyRoute: "GET /v1/sessions",
    legacyPath: "/v1/sessions",
    canonicalRoute: "GET /v1/tables",
    replacementPath: "/v1/tables",
    code: "LEGACY_SESSIONS_ROUTE_RETIRED",
    message: "旧台单列表接口已停止使用，请改用当前前台球台接口。",
  },
  {
    legacyRoute: "GET /v1/coaches/workbench",
    legacyPath: "/v1/coaches/workbench",
    canonicalRoute: "GET /v1/coaches/runtime",
    replacementPath: "/v1/coaches/runtime",
    code: "COACH_WORKBENCH_RETIRED",
    message: "旧版助教工作台数据接口已停用，请改用当前营业状态和今日提成接口。",
  },
] as const;

export type LegacyGatewayReadRouteMigration = (typeof LEGACY_GATEWAY_READ_ROUTE_MIGRATIONS)[number];
export type LegacyGatewayReadRoute = LegacyGatewayReadRouteMigration["legacyRoute"];

export const LegacyRouteRetiredErrorSchema = Type.Object(
  {
    code: Type.Union(
      LEGACY_GATEWAY_READ_ROUTE_MIGRATIONS.map((migration) => Type.Literal(migration.code)),
    ),
    message: Type.String({ minLength: 1, maxLength: 200 }),
    requestId: Type.String({ minLength: 1 }),
    replacementMethod: Type.Literal("GET"),
    replacementPath: Type.Union(
      LEGACY_GATEWAY_READ_ROUTE_MIGRATIONS.map((migration) =>
        Type.Literal(migration.replacementPath),
      ),
    ),
  },
  { additionalProperties: false },
);

export type LegacyRouteRetiredError = Static<typeof LegacyRouteRetiredErrorSchema>;

export function legacyGatewayReadRouteMigration(
  route: string,
): LegacyGatewayReadRouteMigration | undefined {
  return LEGACY_GATEWAY_READ_ROUTE_MIGRATIONS.find((migration) => migration.legacyRoute === route);
}

export function isLegacyRouteRetiredError(
  value: unknown,
  expected: LegacyGatewayReadRouteMigration,
): value is LegacyRouteRetiredError {
  if (!Value.Check(LegacyRouteRetiredErrorSchema, value)) return false;
  const error = value as LegacyRouteRetiredError;
  return (
    error.code === expected.code &&
    error.message === expected.message &&
    error.replacementMethod === "GET" &&
    error.replacementPath === expected.replacementPath
  );
}
